import { avoirsSur } from './impayes';
import { jourIso } from './rapprochement-sorties';
import { refQR, refSCOR } from './swiss-refs';
import { soldeDu, ttcDocument } from './vat';

/**
 * L'argent reçu sur le relevé bancaire (camt.053), relié aux factures.
 *
 * <p>Le relevé montrait les entrées sans rien en faire : le patron devait
 * encore marquer chaque facture payée à la main. Une entrée est reliée à sa
 * facture par la référence du QR-facture que le client a payée, ou, à
 * défaut, par le montant exact et le nom du client dans le libellé — et
 * seulement si une seule facture correspond : on ne devine pas.
 */

const sansAccents = (t) =>
  String(t || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '');

const MOTS_VIDES = new Set(['sarl', 'gmbh', 'societe']);

/** Mots distinctifs d'un nom de client (4 lettres et plus). */
function motsDuNom(nom) {
  return sansAccents(nom)
    .split(/[^a-z0-9]+/)
    .filter((m) => m.length >= 4 && !MOTS_VIDES.has(m));
}

/** Vrai si la référence payée est celle imprimée sur la facture. */
function memeReference(ref, f) {
  const propre = String(ref || '')
    .replace(/\s/g, '')
    .toUpperCase();
  if (!propre) return false;
  const base = f.identifiantReference || f.num;
  if (!base) return false;
  const chiffres = propre.replace(/\D/g, '');
  return (
    (chiffres && chiffres === refQR(base).replace(/\D/g, '')) ||
    propre === refSCOR(base).toUpperCase()
  );
}

const JOUR_MS = 24 * 3600 * 1000;

/** Jours d'écart, en valeur absolue, entre deux dates (ISO ou suisses) ; NaN sans date. */
const joursEntre = (a, b) => Math.abs(Date.parse(jourIso(a)) - Date.parse(jourIso(b))) / JOUR_MS;

/** Jours d'écart admis entre le paiement noté sur une facture et le relevé. */
const ECART_PAIEMENT_JOURS = 2;

/**
 * Relie chaque entrée d'argent du relevé à une facture.
 *
 * <p>La référence ne suffit pas quand le montant dépasse ce qui reste dû : un
 * lot de trois versements lu en une ligne payait toute la somme sur la
 * première facture. Une facture déjà payée (affichage) ne se relie qu'au
 * versement de son jour de paiement (à deux jours près) : le second virement
 * du même client, du même montant, se disait « payée » sans rien faire.
 *
 * @param {object[]} mouvements lignes du relevé ({date, montant, sens, label, ref})
 * @param {object[]} factures factures clients
 * @param {object[]} [clients] clients, pour leur nom
 * @param {object} [options] options
 * @param {boolean} [options.payeesAussi] relier aussi aux factures déjà payées
 *   (pour l'affichage, une fois le paiement enregistré)
 * @returns {Array<{facture: object, par: 'reference'|'nom'}|null>} pour chaque mouvement
 */
export function rapprocheEntrees(mouvements, factures, clients, { payeesAussi = false } = {}) {
  const nomDe = (id) => (clients || []).find((c) => c.id === id)?.nom || '';
  const candidates = (factures || []).filter(
    (f) =>
      f.type !== 'avoir' &&
      (['Envoyée', 'Partielle', 'En retard'].includes(f.statut) ||
        (payeesAussi && f.statut === 'Payée')),
  );
  const prises = new Set();
  return (mouvements || []).map((t) => {
    if (t.sens !== 'CRDT') return null;
    const libres = candidates.filter(
      (f) =>
        !prises.has(f.id) &&
        (f.statut !== 'Payée' || joursEntre(f.paidDate, t.date) <= ECART_PAIEMENT_JOURS),
    );
    let facture = libres.find(
      (f) =>
        memeReference(t.ref, f) && (f.statut === 'Payée' || (+t.montant || 0) <= soldeDu(f) + 0.05),
    );
    let par = 'reference';
    if (!facture) {
      const libelle = sansAccents(t.label);
      const auMontant = libres.filter((f) => {
        const attendu = f.statut === 'Payée' ? ttcDocument(f) : soldeDu(f);
        return Math.abs(attendu - (+t.montant || 0)) < 0.01;
      });
      const trouves = (f) => motsDuNom(nomDe(f.clientId)).filter((m) => libelle.includes(m));
      // Le nom entier passe avant un nom à moitié trouvé : « Exemple SA »
      // ne doit pas hésiter avec « Marie Exemple ».
      const entier = auMontant.filter(
        (f) => trouves(f).length > 0 && trouves(f).length === motsDuNom(nomDe(f.clientId)).length,
      );
      const enPartie = auMontant.filter((f) => trouves(f).length > 0);
      facture =
        entier.length === 1
          ? entier[0]
          : entier.length === 0 && enPartie.length === 1
            ? enPartie[0]
            : null;
      par = 'nom';
    }
    if (!facture) return null;
    prises.add(facture.id);
    return { facture, par };
  });
}

/**
 * Les paiements reçus qu'un relevé importé encaisse d'office : chaque entrée
 * reconnue ({@link rapprocheEntrees}), sauf un doublon possible d'un autre
 * relevé, et sauf le paiement déjà noté sur la même facture (même montant, à
 * deux jours près) — le camt puis le PDF du même mois l'encaissaient deux fois.
 *
 * @param {object[]} nouveaux mouvements ajoutés par l'import
 * @param {object[]} factures factures clients
 * @param {object[]} [clients] clients
 * @param {object[]} [gardes] relevé déjà gardé (ses lignes notent `factureId`)
 * @returns {Array<{facture: object, par: string, t: object}>} les encaissements
 */
export function encaissementsDuReleve(nouveaux, factures, clients, gardes = []) {
  const deja = (gardes || []).filter((g) => g.factureId != null);
  const lignes = (nouveaux || []).map((t) =>
    t.doublonPossible || t.traite ? { ...t, sens: 'traite' } : t,
  );
  return rapprocheEntrees(lignes, factures, clients)
    .map((l, i) => l && { ...l, t: nouveaux[i] })
    .filter(Boolean)
    .filter(
      ({ facture, t }) =>
        !deja.some(
          (g) =>
            String(g.factureId) === String(facture.id) &&
            Math.abs((+g.montant || 0) - (+t.montant || 0)) < 0.005 &&
            joursEntre(g.date, t.date) <= ECART_PAIEMENT_JOURS &&
            (!g.ref || !t.ref || String(g.ref) === String(t.ref)),
        ),
    );
}

/** Statuts d'une facture qui attend encore de l'argent. */
export const STATUTS_A_PAYER = ['Envoyée', 'Partielle', 'En retard'];

/** Statuts d'une offre dont l'acompte peut arriver : acceptée, ou en attente de réponse. */
const STATUTS_OFFRE_ACOMPTE = ['Accepté', 'En attente'];

const egal = (a, b) => Math.abs((+a || 0) - (+b || 0)) < 0.01;

/** Mots d'un libellé ou d'un nom, un à un : « Chevrot-Leblond » en donne deux. */
const motsDe = (texte) =>
  sansAccents(texte)
    .split(/[^a-z0-9]+/)
    .filter((m) => m.length >= 4 && !MOTS_VIDES.has(m));

/**
 * Le client nommé par le libellé d'un versement.
 *
 * <p>Le nom entier passe avant un nom à moitié trouvé : « Crédit Sylvie
 * Chevrot-Leblond » désigne Mme Chevrot-Leblond, même si une « Sylvie Martin »
 * est aussi cliente. Entre deux noms entiers, le plus long l'emporte
 * (« Marie Exemple » avant « Exemple SA ») ; à égalité, on ne devine pas.
 * Un nom trouvé en partie ne compte que s'il est seul, et pas par son seul
 * premier mot (souvent le prénom) : « Sylvie Martin » ne doit pas désigner
 * Sylvie Chevrot-Leblond.
 *
 * @param {string} libelle libellé du mouvement
 * @param {object[]} [clients] clients ({id, nom})
 * @returns {object|null} le client, ou null
 */
export function clientDuLibelle(libelle, clients) {
  const dansLibelle = new Set(motsDe(libelle));
  if (!dansLibelle.size) return null;
  const notes = (clients || [])
    .map((c) => {
      const mots = motsDe(c.nom);
      return { c, mots, trouves: mots.filter((m) => dansLibelle.has(m)) };
    })
    .filter((n) => n.trouves.length > 0);
  const entiers = notes.filter((n) => n.trouves.length === n.mots.length);
  if (entiers.length) {
    const plusLong = Math.max(...entiers.map((n) => n.mots.length));
    const meilleurs = entiers.filter((n) => n.mots.length === plusLong);
    return meilleurs.length === 1 ? meilleurs[0].c : null;
  }
  const enPartie = notes.filter(
    (n) => n.trouves.length >= 2 || (n.trouves.length === 1 && n.trouves[0] !== n.mots[0]),
  );
  return enPartie.length === 1 ? enPartie[0].c : null;
}

/**
 * La facture d'acompte d'une offre, si le serveur l'a déjà établie.
 *
 * @param {object} offre offre
 * @param {object[]} [factures] factures clients
 * @returns {object|null} la facture d'acompte, ou null
 */
export function factureDAcompte(offre, factures) {
  return (
    (factures || []).find(
      (f) =>
        f.type === 'acompte' &&
        f.statut !== 'Annulée' &&
        String(f.devisId) === String(offre.id) &&
        (!offre.acompteFacture || String(f.num) === String(offre.acompteFacture)),
    ) || null
  );
}

/**
 * Les offres dont l'acompte est attendu : acceptées ou en attente, avec un
 * acompte demandé, et dont la facture d'acompte manque ou n'est pas payée.
 *
 * @param {object[]} [devis] offres
 * @param {object[]} [factures] factures clients
 * @returns {Array<{offre: object, facture: object|null, reste: number}>} les acomptes attendus
 */
export function acomptesAttendus(devis, factures) {
  return (devis || [])
    .filter((d) => STATUTS_OFFRE_ACOMPTE.includes(d.statut))
    .map((offre) => {
      // Le montant calculé par le serveur (TVA comprise) ; l'acompte écrit en
      // francs à défaut.
      const montant = +offre.montantAcompte || +offre.acompteMontant || 0;
      const facture = factureDAcompte(offre, factures);
      return { offre, facture, montant, reste: facture ? soldeDu(facture) : montant };
    })
    .filter(
      (a) =>
        a.montant > 0 && a.reste > 0 && (!a.facture || STATUTS_A_PAYER.includes(a.facture.statut)),
    )
    .map(({ offre, facture, reste }) => ({ offre, facture, reste }));
}

/**
 * Ce qu'un versement reçu peut payer, et qui l'a fait.
 *
 * <p>« Crédit Sylvie Chevrot-Leblond +11'000 » ne proposait qu'une facture
 * d'un autre client : Mme Chevrot-Leblond payait l'acompte de son offre, dont
 * la facture n'existait pas encore. Le client se reconnaît d'abord à la
 * référence payée (celle d'une facture, ou celle du QR de l'offre qui sert à
 * l'acompte), sinon à son nom dans le libellé, sinon au montant quand une
 * seule pièce l'attend. Ses factures ouvertes et les acomptes de ses offres
 * sont proposés ; la référence ou le montant exact désigne d'avance la
 * bonne — le patron confirme toujours.
 *
 * @param {object} t mouvement du relevé ({montant, sens, label, ref})
 * @param {object} donnees données de la société
 * @param {object[]} [donnees.factures] factures clients
 * @param {object[]} [donnees.clients] clients
 * @param {object[]} [donnees.devis] offres (absentes pour un rôle qui ne les voit pas)
 * @returns {{client: object|null, par: 'reference'|'nom'|'montant'|null,
 *   factures: Array<{facture: object, reste: number}>,
 *   acomptes: Array<{offre: object, facture: object|null, reste: number}>,
 *   autres: Array<{facture: object, reste: number}>,
 *   autresAcomptes: Array<{offre: object, facture: object|null, reste: number}>,
 *   offresSansAcompte: object[],
 *   propose: {genre: 'facture'|'acompte', id: *}|null}} les propositions
 */
export function propositionsEntree(t, { factures, clients, devis } = {}) {
  const montant = +t?.montant || 0;
  const attendus = acomptesAttendus(devis, factures);
  // La facture d'acompte d'une offre se propose sous le nom de l'offre : le
  // patron pense « l'acompte de l'offre n° 12 », pas « la facture n° 57 ».
  const dAcompte = new Set(attendus.filter((a) => a.facture).map((a) => a.facture.id));
  const ouvertes = (factures || [])
    .filter((f) => f.type !== 'avoir' && STATUTS_A_PAYER.includes(f.statut) && !dAcompte.has(f.id))
    .map((facture) => ({ facture, reste: soldeDu(facture) }));
  const parId = (id) => (clients || []).find((c) => c.id === id) || { id, nom: '' };

  let client = null;
  let par = null;
  let propose = null;
  // 1. La référence payée : celle d'une facture, ou celle de l'offre (son QR
  //    sert à verser l'acompte, et la facture d'acompte la reprend).
  const parRef = ouvertes.find((o) => memeReference(t?.ref, o.facture));
  const acompteRef =
    !parRef &&
    attendus.find(
      (a) =>
        (a.offre.identifiantReference &&
          memeReference(t?.ref, { identifiantReference: a.offre.identifiantReference })) ||
        (a.facture && memeReference(t?.ref, a.facture)),
    );
  if (parRef) {
    client = parId(parRef.facture.clientId);
    par = 'reference';
    propose = { genre: 'facture', id: parRef.facture.id };
  } else if (acompteRef) {
    client = parId(acompteRef.offre.clientId);
    par = 'reference';
    propose = { genre: 'acompte', id: acompteRef.offre.id };
  }
  // 2. Le nom du client dans le libellé.
  if (!client) {
    client = clientDuLibelle(t?.label, clients);
    if (client) par = 'nom';
  }
  // 3. Le montant, quand une seule pièce attend exactement cette somme.
  if (!client) {
    const auMontant = [
      ...ouvertes
        .filter((o) => egal(o.reste, montant))
        .map((o) => ({ clientId: o.facture.clientId, genre: 'facture', id: o.facture.id })),
      ...attendus
        .filter((a) => egal(a.reste, montant))
        .map((a) => ({ clientId: a.offre.clientId, genre: 'acompte', id: a.offre.id })),
    ];
    if (auMontant.length === 1) {
      const [seul] = auMontant;
      client = parId(seul.clientId);
      par = 'montant';
      propose = { genre: seul.genre, id: seul.id };
    }
  }

  const aLui = (clientId) => client != null && clientId === client.id;
  const facturesDuClient = ouvertes.filter((o) => aLui(o.facture.clientId));
  const acomptesDuClient = attendus.filter((a) => aLui(a.offre.clientId));
  // Le montant exact désigne la pièce, s'il n'en désigne qu'une.
  if (!propose && client) {
    const exactes = [
      ...facturesDuClient
        .filter((o) => egal(o.reste, montant))
        .map((o) => ({ genre: 'facture', id: o.facture.id })),
      ...acomptesDuClient
        .filter((a) => egal(a.reste, montant))
        .map((a) => ({ genre: 'acompte', id: a.offre.id })),
    ];
    if (exactes.length === 1) propose = exactes[0];
  }
  const avecAcompte = new Set(attendus.map((a) => a.offre.id));
  return {
    client,
    par,
    factures: facturesDuClient,
    acomptes: acomptesDuClient,
    autres: ouvertes.filter((o) => !aLui(o.facture.clientId)),
    autresAcomptes: attendus.filter((a) => !aLui(a.offre.clientId)),
    // Ses offres sans acompte à payer : à ouvrir pour y demander l'acompte.
    offresSansAcompte: client
      ? (devis || []).filter(
          (d) =>
            aLui(d.clientId) && STATUTS_OFFRE_ACOMPTE.includes(d.statut) && !avecAcompte.has(d.id),
        )
      : [],
    propose,
  };
}

/**
 * La facture mise à jour par un paiement reçu : payée si le montant couvre
 * ce qu'il reste dû, sinon partiellement payée.
 *
 * @param {object} f facture
 * @param {number} montant montant reçu
 * @param {string} date jour du paiement, écrit à la suisse
 * @param {object[]} [factures] toutes les factures, pour les avoirs émis sur celle-ci
 * @returns {object} la facture mise à jour
 */
export function factureApresPaiement(f, montant, date, factures = []) {
  const paye = Math.round(((+f.paidAmount || 0) + (+montant || 0)) * 100) / 100;
  // Un avoir réduit ce que le client doit : payée au net, la facture est
  // payée, et non « Partielle » pour toujours.
  const credit = avoirsSur(f, factures).reduce((s, a) => s + ttcDocument(a), 0);
  const payee = paye >= ttcDocument(f) - credit - 0.05;
  return { ...f, statut: payee ? 'Payée' : 'Partielle', paidAmount: paye, paidDate: date };
}
